import { useState, useEffect } from "react";
import {
  Typography,
  CircularProgress,
  Box,
  Button,
  Avatar,
} from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import FavoriteIcon from "@mui/icons-material/Favorite";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import DescriptionIcon from "@mui/icons-material/Description";
import NotesDialog from "./NotesDialog";
import HelpDialog from "./HelpDialog";
import request from "../../../utilities/requests";
import { toast } from "../../../utilities/SnackBarUtils";

const NO_PARTICIPATE_MESSAGES = [
  "Cheer from the sidelines this time!",
  "Not your round to shine, but keep cheering!",
  "The heart's on pause for you this time.",
  "Sit tight, your match might be up next!",
  "This one's not your flavor, stay tuned!",
  "Cupid says: take a breather this round.",
  "Your turn to spectate, grab some popcorn.",
  "Let love pass this round, your shot's coming!",
  "Not your match moment, but who knows next?",
  "On the bench this time, coach!",
  "Cupid's arrow skipped this round, hang tight!",
  "No heart button for you this time, enjoy the show!",
  "Off the hook for now, but love is patient.",
  "Benched for this play, but the game's not over!",
  "Spectator mode: ON. Player mode: NEXT TIME.",
  "This contestant isn't your match, but someone else might be!",
  "Relax and sip your drink, your chance could be next.",
  "Heart lockout: temporary only!",
  "Love referee says: not in this round.",
  "Audience applause only for you this time!",
];

const Pitch = ({ eventId, contestant, round, like, setLike, canLike }) => {
  const [saving, setSaving] = useState(false);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [noParticipateMsg, setNoParticipateMsg] = useState("");

  useEffect(() => {
    setNoParticipateMsg(
      NO_PARTICIPATE_MESSAGES[
        Math.floor(Math.random() * NO_PARTICIPATE_MESSAGES.length)
      ]
    );
  }, []);

  const handleClickLike = async () => {
    if (saving || !contestant?._id) return;
    const next = !like;
    setSaving(true);
    setLike(next);
    const result = await request("dealbreakers/round/like", "post", {
      round,
      contestantId: contestant._id,
      like: next,
    });
    if (!result?.success) {
      setLike(!next);
      toast.error(result?.error || "Could not save that. Please try again.");
    }
    setSaving(false);
  };

  const matchScore = contestant?.matchScore || 0;

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          flex: "0 0 50%",
          overflow: "hidden",
          borderBottomLeftRadius: "10rem",
          borderBottomRightRadius: "10rem",
          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.3)",
          position: "relative",
        }}
      >
        {contestant?.profileImage ? (
          <img
            src={contestant.profileImage}
            alt="profileImg"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        ) : (
          <AccountCircleIcon
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              color: "grey",
            }}
          />
        )}
      </div>

      <div
        style={{
          flex: "0 0 25%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 10,
        }}
      >
        <Typography variant="h5">{contestant?.firstName}</Typography>
        {canLike && (
          <>
            <Box
              sx={{
                position: "relative",
                display: "inline-flex",
                transform: "rotate(180deg)",
              }}
            >
              <CircularProgress
                variant="determinate"
                value={matchScore}
                size={50}
                thickness={4}
                color="primary"
                aria-label={`Match score is ${matchScore}%`}
              />
              <Box
                sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: "absolute",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ fontWeight: "bold", transform: "rotate(180deg)" }}
                >
                  {`${matchScore}%`}
                </Typography>
              </Box>
            </Box>
            <Typography variant="caption">Match Score</Typography>
          </>
        )}
      </div>

      {canLike ? (
        <div
          style={{
            flex: "0 0 15%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-around",
          }}
        >
          <HelpOutlineIcon
            style={{ color: "grey", cursor: "pointer", fontSize: 30 }}
            onClick={() => setHelpDialogOpen(true)}
          />
          <Button
            style={{ borderRadius: "50%" }}
            onClick={handleClickLike}
            disabled={saving}
            aria-pressed={like}
            aria-label={
              like ? "Withdraw yourself from this round" : "Put yourself forward"
            }
          >
            <Avatar
              style={{
                height: 60,
                width: 60,
                backgroundColor: like === true ? "#68C371" : "grey",
              }}
            >
              <FavoriteIcon style={{ height: 30, width: 30 }} />
            </Avatar>
          </Button>
          <DescriptionIcon
            style={{ color: "grey", cursor: "pointer", fontSize: 30 }}
            onClick={() => setNotesDialogOpen(true)}
          />
        </div>
      ) : (
        <div
          style={{
            flex: "0 0 15%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 24px",
          }}
        >
          <Typography align="center">{noParticipateMsg}</Typography>
        </div>
      )}

      <div
        style={{
          flex: "1",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      ></div>

      <HelpDialog open={helpDialogOpen} close={() => setHelpDialogOpen(false)} />
      <NotesDialog
        open={notesDialogOpen}
        close={() => setNotesDialogOpen(false)}
        otherUser={contestant?._id}
        eventId={eventId}
      />
    </Box>
  );
};

export default Pitch;
